import React, { useEffect, useMemo, useRef, useState } from 'react'
import moment from 'moment'
import { useScrollLock } from '../../../../../_utils/useScrollLock'
import { hapticTick } from '../../../../../_utils/haptics'
import { POST_TYPES, TYPE_ORDER, makePost, designName, designsFor, showcaseFor, layoutOf } from '../data/postTypes'
import { FAMILIES } from '../render/families'
import { COLOURWAYS } from '../render/colourways'
import { postTheme } from '../render/render'
import { pickTimes, daysFor } from '../data/freeSlots'
import { dayLabel, isoDay } from '../data/dates'
import { seasonsFrom, seasonalLayouts, withSalon } from '../kits'
import LazyPreview from './studio/LazyPreview'
import { useSeasonJump } from './studio/useSeasonJump'
import { useFreeDays, useWhose, whoPatch, WhosePicker } from './studio/SlotPicker'
import BaileyAsk from './BaileyAsk'
import { useLiveCodes } from '../../../../discounts/discountStore'
import { offerWords } from '../../../../discounts/data/codes'
import { DISCOUNTS_VISIBLE } from '../../../../../config/featureFlags'
import { Seg, PostCard, FavouriteGrid } from './cards'
import { CloseIcon, HeartIcon, TypeIcon, SparkIcon } from './icons'

const firstName = (s) => String(s || '').trim().split(/\s+/)[0] || ''
const saidDay = (iso) => { const l = dayLabel(iso); return /^(Today|Tomorrow)$/.test(l) ? l.toLowerCase() : `on ${l}` }
const SLOT_JOBS = ['freeSlots', 'oneSlot']

// A season's designs as posts, with the salon's name in their words
export const seasonalPosts = (seasonId, { trade = 'hair', format = 'post', salonName = '' } = {}) => seasonalLayouts(seasonId).map((layout) => {
  const post = makePost('announce', { trade, family: 'layouts', layout, format })
  return { layout, post: { ...post, content: withSalon(post.content, salonName) } }
})

// A job's designs (the three styles, then its layouts), each with the
// salon's own details where there are some: the diary's free times (one
// stylist's when `who` is set), their newest kind review, someone on the
// team. Nothing is made up: without them the design keeps its example words.
export const galleryPosts = (type, { trade = 'hair', brandColour = null, inBrand = false, format = 'post', slots = null, who = null, review = null, member = null, code = null } = {}) => {
  const show = showcaseFor(type)
  return designsFor(type).map(({ family, layout }) => {
    const brand = inBrand && brandColour
    const colourway = brand ? 'brand' : layout ? undefined : (show.family === family && !show.layout ? show.colourway : COLOURWAYS[family][0].id)
    const content = {}
    let photos
    let source = null
    if (SLOT_JOBS.includes(type) && slots && slots.slots.length) {
      // The booking layouts take four times, the styles three
      const picks = pickTimes(slots.slots, type === 'oneSlot' ? 1 : layout ? 4 : 3).map(s => s.label)
      if (type === 'oneSlot') Object.assign(content, { date: slots.date, time: picks[0] })
      else Object.assign(content, { date: slots.date, times: picks, more: slots.slots.length > picks.length })
      if (who) Object.assign(content, whoPatch(who))
      source = `slots:${slots.date}`
    }
    if (type === 'review' && review) {
      Object.assign(content, { quote: review.text, name: review.firstName || 'A client', service: review.service || '', rating: review.rating })
      source = `review:${review.id}`
    }
    // An offer made for one of the salon's live codes (from Discounts' Share)
    if (type === 'offer' && code) {
      // Never the design's example price next to a real code: the salon
      // adds the full price themselves if they want one
      Object.assign(content, offerWords(code), { price: '' })
      source = `code:${code.id}`
    }
    if (type === 'team' && member) {
      Object.assign(content, { name: firstName(member.name), ...(member.role ? { role: member.role } : {}) })
      if (member.image) photos = { main: { src: member.image, fx: 0.5, fy: 0.35, zoom: 1 } }
      source = `team:${member.id}`
    }
    const post = makePost(type, { trade, family, layout, colourway, brandColour: brand ? brandColour : null, content, photos, source, format })
    return { family, layout, post }
  })
}

// Every season's designs in one scroll, the next ones first, under chips
// that jump to a season and follow the scroll
const SeasonalGallery = ({ seasons, trade, kit, format, onFormat, onPick }) => {
  const salonName = (kit && kit.name) || ''
  const all = useMemo(() => seasons.map(season => ({ season, items: seasonalPosts(season.id, { trade, format, salonName }) })), [seasons, trade, format, salonName])
  const { barRef, active, jump } = useSeasonJump(seasons[0] && seasons[0].id)
  return (
    <>
      <div className="sx-np-bar">
        <div className="sx-np-bar-text">
          <h3>Seasonal</h3>
          <p>Twelve designs for every season, the next ones first, each drawn as a post and a Story with your name on. Pick one to make it yours.</p>
        </div>
        <div className="sx-np-controls">
          <Seg label="Format" value={format} onChange={onFormat} options={[{ id: 'post', label: 'Post' }, { id: 'story', label: 'Story' }]} />
        </div>
      </div>
      <div className="sx-np-seasons" ref={barRef} role="navigation" aria-label="Go to a season">
        {seasons.map(x => (
          <button key={x.id} type="button" data-chip={x.id} aria-current={x.id === active ? 'true' : undefined} className={`sx-chip${x.id === active ? ' is-on' : ''}`} onClick={() => { hapticTick(); jump(x.id) }}>{x.name}</button>
        ))}
      </div>
      {all.map(({ season, items }) => (
        <section key={season.id} className="sx-np-season" data-season={season.id} aria-label={season.name}>
          <h4 className="sx-np-season-title">{season.name}</h4>
          <div className="sx-np-grid">
            {items.map(({ layout, post }) => {
              const l = layoutOf('announce', layout)
              return (
                <div key={layout} className="sx-tpl-cell">
                  <button type="button" className="sx-tpl" onClick={() => onPick(post)}>
                    <span className="sx-tpl-art"><LazyPreview post={post} theme={postTheme(post, { kit })} format={format} /></span>
                    <span className="sx-tpl-name">{l.name}</span>
                    <span className="sx-tpl-design">{l.purpose}</span>
                  </button>
                </div>
              )
            })}
          </div>
        </section>
      ))}
    </>
  )
}

// "What do you want to post?": Bailey's picks and every design for each
// kind of post, filled in with the salon's own details, one tap from the
// studio. Opened from Create, the calendar (for a day), Marketing home and
// the Social page's templates (at that kind of post).
const NewPost = ({
  plan = {}, kit, trade, brandColour, ideas, favourites = [], review, member,
  view = 'designed', onView, isFav, onFav, onPick, onPickFavourite, onRemoveFavourite,
  onAsk, asking, askNote, onClose,
}) => {
  useScrollLock(true)
  const { days: all } = useFreeDays()
  const { team, me, first, canChoose } = useWhose(all)
  // Whose free times: as asked (the diary's stylist menu), as picked here,
  // else the owner's everyone and a stylist their own
  const [whoPick, setWhoPick] = useState(plan.staffId ? String(plan.staffId) : undefined)
  const whoId = whoPick !== undefined ? (whoPick === 'all' ? null : whoPick) : (first ? first.id : null)
  const who = whoId ? (team.find(t => t.id === whoId) || (String(plan.staffId || '') === whoId && plan.staffName ? { id: whoId, name: plan.staffName, firstName: firstName(plan.staffName) } : null)) : null
  const days = useMemo(() => (all ? daysFor(all, whoId) : null), [all, whoId])
  const [format, setFormat] = useState('post')
  const inBrand = view === 'brand' && !!brandColour
  const dayIso = plan.day ? isoDay(new Date(plan.day)) : null
  // The free times this is about: the day asked for (or the next with some,
  // when it has none), else the first with some
  const slots = useMemo(() => {
    if (!days) return null
    if (dayIso) return days.find(d => d.date === dayIso) || days.find(d => d.date > dayIso) || null
    return days[0] || null
  }, [days, dayIso])
  const movedFrom = dayIso && slots && slots.date !== dayIso ? dayIso : null
  // From the calendar: that day's free times, ready to go
  const dayIdea = useMemo(() => {
    if (!dayIso || !slots || !slots.slots.length) return null
    const one = slots.slots.length === 1
    const type = one ? 'oneSlot' : 'freeSlots'
    const show = showcaseFor(type)
    const picks = pickTimes(slots.slots, one ? 1 : 3).map(s => s.label)
    const post = makePost(type, {
      trade, family: show.family, layout: show.layout, colourway: inBrand ? 'brand' : show.colourway, brandColour: inBrand ? brandColour : null, format: 'story',
      content: { ...(one ? { date: slots.date, time: picks[0] } : { date: slots.date, times: picks, more: slots.slots.length > picks.length }), ...(who ? whoPatch(who) : {}) },
      source: `slots:${slots.date}`,
    })
    const said = dayLabel(slots.date)
    const when = /^(Today|Tomorrow)$/.test(said) ? said.toLowerCase() : `on ${said}`
    const whose = who ? (who.id === me ? 'You have ' : `${who.firstName} has `) : ''
    return {
      post,
      title: one ? `${whose ? `${whose}a` : 'A'} free slot ${when}` : `${whose}${slots.slots.length} free times ${when}`,
      why: 'From your diary. A quick Story fills them fastest.',
    }
  }, [dayIso, slots, trade, inBrand, brandColour, who, me])
  // Opened for a day: that day's free times take the place of the week's
  const shownIdeas = useMemo(() => (ideas || []).filter(({ idea }) => !(dayIdea && idea.kind === 'slots')), [ideas, dayIdea])
  const picks = shownIdeas.length + (dayIdea ? 1 : 0)
  const [tab, setTab] = useState(() => plan.type || 'forYou')
  const seasons = useMemo(() => seasonsFrom(), [])
  useEffect(() => { if (plan.type) setTab(plan.type) }, [plan.type])

  const bodyRef = useRef(null)
  const closeRef = useRef(onClose)
  closeRef.current = onClose
  // Pickers opened from here (whose free times) sit above the sheet
  useEffect(() => {
    document.body.classList.add('sx-np-open')
    return () => document.body.classList.remove('sx-np-open')
  }, [])
  useEffect(() => {
    const back = document.activeElement
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); closeRef.current() } }
    window.addEventListener('keydown', onKey, true)
    return () => {
      window.removeEventListener('keydown', onKey, true)
      if (back && back.focus && document.contains(back)) back.focus({ preventScroll: true })
    }
  }, [])
  const go = (id) => {
    if (id === tab) return
    hapticTick()
    setTab(id)
    if (bodyRef.current) bodyRef.current.scrollTop = 0
  }

  const title = plan.day
    ? `A post for ${moment(plan.day).format('dddd D MMMM')}${plan.at ? ` at ${moment(plan.at).format('h:mma').replace(':00', '')}` : ''}`
    : 'What do you want to post?'
  const nav = [
    { id: 'forYou', label: 'For you', icon: <SparkIcon size={16} /> },
    ...(favourites.length ? [{ id: 'favourites', label: 'Your favourites', icon: <HeartIcon size={16} /> }] : []),
    ...(seasons.length ? [{ id: 'seasonal', label: 'Seasonal', icon: <TypeIcon type="announce" size={16} /> }] : []),
    { rule: true },
    ...TYPE_ORDER.map(type => ({ id: type, label: POST_TYPES[type].label, icon: <TypeIcon type={type} size={16} /> })),
  ]
  const job = POST_TYPES[tab] ? tab : null
  // Opened from a code's Share: offers come with that code on
  const live = useLiveCodes()
  const offerCode = DISCOUNTS_VISIBLE && plan.codeId ? (live.codes.find((c) => c.id === plan.codeId) || null) : null
  const items = useMemo(() => (job ? galleryPosts(job, { trade, brandColour, inBrand, format, slots, who, review, member, code: offerCode }) : []), [job, trade, brandColour, inBrand, format, slots, who, review, member, offerCode])
  const slotJob = !!job && SLOT_JOBS.includes(job)
  const withSlots = slotJob && slots && slots.slots.length
  const slotsDay = withSlots ? dayLabel(slots.date) : ''
  const slotsSaid = /^(Today|Tomorrow)$/.test(slotsDay) ? slotsDay.toLowerCase() : slotsDay
  const slotsWhen = /^(Today|Tomorrow)$/.test(slotsDay) ? slotsDay.toLowerCase() : `on ${slotsDay}`
  const whose = who ? (who.id === me ? 'your' : `${who.firstName}’s`) : ''
  const pickWho = (id) => { hapticTick(); setWhoPick(id) }

  return (
    <div className="sx-sheet-backdrop sx-np-backdrop" onPointerDown={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="sx-np" role="dialog" aria-modal="true" aria-labelledby="sx-np-title">
        <div className="sx-np-head">
          <h2 className="sx-sheet-title" id="sx-np-title">{title}</h2>
          <button type="button" className="sx-icon-btn" onClick={onClose} aria-label="Close"><CloseIcon /></button>
        </div>
        <div className="sx-np-ask">
          <BaileyAsk onAsk={onAsk} busy={asking} note={askNote} starters={false} />
        </div>
        <div className="sx-np-main">
          <nav className="sx-np-nav" aria-label="What to post">
            {nav.map((n, i) => (n.rule ? <span key={`rule-${i}`} className="sx-np-nav-rule" aria-hidden /> : (
              <button key={n.id} type="button" className={`sx-np-nav-btn${tab === n.id ? ' is-on' : ''}`} aria-current={tab === n.id ? 'true' : undefined} onClick={() => go(n.id)}>
                {n.icon}
                <span>{n.label}</span>
              </button>
            )))}
          </nav>
          <div className="sx-np-body" ref={bodyRef}>
            {tab === 'forYou' && (
              <>
                <div className="sx-np-bar">
                  <div className="sx-np-bar-text">
                    <h3>For you</h3>
                    <p>{plan.day ? 'Ready to go, from your diary and reviews.' : 'Bailey’s picks this week, from your diary and reviews.'}</p>
                  </div>
                </div>
                {ideas === null && <div className="sx-cards">{[0, 1, 2].map(i => <div key={i} className="sx-card is-loading" aria-hidden><span className="sx-card-art" /><span className="sx-card-text"><span className="sx-line" /><span className="sx-line is-short" /></span></div>)}</div>}
                {ideas !== null && picks > 0 && (
                  <div className="sx-cards">
                    {dayIdea && <PostCard post={dayIdea.post} theme={postTheme(dayIdea.post, { kit })} kind="Story" title={dayIdea.title} why={dayIdea.why} onClick={() => onPick(dayIdea.post)} />}
                    {shownIdeas.map(({ idea, post }) => (
                      <PostCard key={idea.id} post={post} theme={postTheme(post, { kit })} kind={post.format === 'story' ? 'Story' : 'Post'} title={idea.title} why={idea.why} onClick={() => onPick(post)} />
                    ))}
                  </div>
                )}
                {ideas !== null && !picks && <p className="sx-empty">Nothing needs a post right now. Pick a kind of post to see its designs.</p>}
              </>
            )}
            {tab === 'favourites' && (
              <>
                <div className="sx-np-bar">
                  <div className="sx-np-bar-text">
                    <h3>Your favourites</h3>
                    <p>The designs you use most. Tap one to start a new post in it.</p>
                  </div>
                </div>
                {favourites.length
                  ? <FavouriteGrid favourites={favourites} kit={kit} trade={trade} brandColour={brandColour} onPick={onPickFavourite} onRemove={onRemoveFavourite} />
                  : <p className="sx-empty">Heart a design and it’s kept here.</p>}
              </>
            )}
            {tab === 'seasonal' && <SeasonalGallery seasons={seasons} trade={trade} kit={kit} format={format} onFormat={setFormat} onPick={onPick} />}
            {job && (
              <>
                <div className="sx-np-bar">
                  <div className="sx-np-bar-text">
                    <h3>{POST_TYPES[job].label}</h3>
                    <p>
                      {POST_TYPES[job].blurb}. {items.length} designs
                      {withSlots ? (whose ? `, with ${whose} free times ${slotsWhen}` : `, with ${slotsSaid}’s free times from your diary`) : ''}
                      {job === 'review' && review ? `, with ${review.firstName || 'a client'}’s review` : ''}.
                      {slotJob && whose && days && !days.length ? ` Nothing free in ${whose} diary in the next week, so these show example times.` : ''}
                      {slotJob && withSlots && movedFrom ? ` Nothing free in ${whose || 'your'} diary ${saidDay(movedFrom)}, so these use ${saidDay(slots.date)}.` : ''}
                    </p>
                  </div>
                  <div className="sx-np-controls">
                    {slotJob && canChoose && <WhosePicker team={team} me={me} value={whoId} extra={who ? { id: who.id, name: who.name || who.firstName } : null} onChange={pickWho} />}
                    <Seg label="Format" value={format} onChange={setFormat} options={[{ id: 'post', label: 'Post' }, { id: 'story', label: 'Story' }]} />
                    {brandColour && <Seg label="Colours" value={inBrand ? 'brand' : 'designed'} onChange={onView} options={[{ id: 'designed', label: 'As designed' }, { id: 'brand', label: 'In your brand' }]} />}
                  </div>
                </div>
                <div className="sx-np-grid">
                  {items.map(({ family, layout, post }) => {
                    const fav = isFav ? isFav(post) : false
                    const l = layout && layoutOf(job, layout)
                    return (
                      <div key={layout || family} className="sx-tpl-cell">
                        <button type="button" className="sx-tpl" onClick={() => onPick(post)}>
                          <span className="sx-tpl-art"><LazyPreview post={post} theme={postTheme(post, { kit })} format={format} /></span>
                          <span className="sx-tpl-name">{designName(job, family, layout)}</span>
                          <span className="sx-tpl-design">{l ? l.blurb : FAMILIES[family].name}</span>
                        </button>
                        {onFav && (
                          <button type="button" className={`sx-tpl-fav${fav ? ' is-on' : ''}`} aria-pressed={fav} aria-label={fav ? `${designName(job, family, layout)} is a favourite. Tap to take it out` : `Save ${designName(job, family, layout)} as a favourite`} onClick={(e) => onFav(post, e.currentTarget)}>
                            <HeartIcon size={15} filled={fav} />
                          </button>
                        )}
                      </div>
                    )
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default NewPost
